/** @format */

import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import {
  Alert,
  Box,
  Button,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { HasErrors } from "../common/Logic/ServerResponse";
import { getRawQueryParam } from "../common/query/getRawQueryParam";
import { fetchPublicDashboard } from "../features/admin/adminApi";
import ApplicationStatusReport from "../features/admin/components/Dashboard/ApplicationStatusReport";
import DisplayCharts from "../features/admin/components/Dashboard/DisplayCharts";
import "../features/admin/components/Dashboard/dashboard-print.css";
import { exportDashboardSnapshotCsv } from "../features/admin/components/Dashboard/exportDashboardCsv";
import { DasboardDto } from "../features/admin/models";

export default function PublicReportingDashboard() {
  const { search } = useLocation();
  const key = getRawQueryParam(search, "key");

  const [dashboardYear, setDashboardYear] = useState(() =>
    new Date().getFullYear()
  );
  const [publicPayload, setPublicPayload] = useState<{
    data: DasboardDto | null;
    loading: boolean;
  }>({ data: null, loading: true });
  const [priorYearData, setPriorYearData] = useState<DasboardDto | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const loadSeq = useRef(0);
  const priorSeq = useRef(0);

  const load = useCallback(async () => {
    if (!key.trim()) {
      setLoadError(
        "Add your shared reporting key to the URL, for example: /reporting?key=…"
      );
      setPublicPayload({ data: null, loading: false });
      return;
    }

    const seq = ++loadSeq.current;
    setPublicPayload({ data: null, loading: true });
    setLoadError(null);

    try {
      const res = await fetchPublicDashboard(dashboardYear, key);
      if (seq !== loadSeq.current) return;
      const ok =
        HasErrors({ payload: res }) === 2 || HasErrors({ payload: res }) === 3;
      if (!ok) {
        setLoadError(
          "Unable to load the dashboard. Check the key with your administrator."
        );
        setPublicPayload({ data: null, loading: false });
        return;
      }
      setPublicPayload({ data: res.data as DasboardDto, loading: false });
    } catch (e) {
      if (seq !== loadSeq.current) return;
      if (axios.isAxiosError(e) && e.response?.status === 404) {
        setLoadError(
          "Reporting returned 404: the key was not accepted, or public reporting is off. Set Values:PublicDashboardKey on the API to exactly match this URL’s key. If the key contains “+”, paste it using %2B instead of + in the address bar, or pick a key without special URL characters."
        );
      } else {
        setLoadError(
          "Unable to reach the reporting service. Confirm the API is running, AxiosCall baseURL matches the API host, and CORS allows this site."
        );
      }
      setPublicPayload({ data: null, loading: false });
    }
  }, [dashboardYear, key]);

  const loadPriorYear = useCallback(async () => {
    if (!key.trim()) {
      setPriorYearData(null);
      return;
    }
    const year = dashboardYear - 1;
    const seq = ++priorSeq.current;
    try {
      const res = await fetchPublicDashboard(year, key);
      if (seq !== priorSeq.current) return;
      const ok =
        HasErrors({ payload: res }) === 2 || HasErrors({ payload: res }) === 3;
      setPriorYearData(ok ? (res.data as DasboardDto) : null);
    } catch {
      if (seq !== priorSeq.current) return;
      setPriorYearData(null);
    }
  }, [dashboardYear, key]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadPriorYear();
  }, [loadPriorYear]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: 3 }}>
      <Container maxWidth="lg">
        {loadError && (
          <Alert severity="warning" className="no-print">
            {loadError}
          </Alert>
        )}
        <Stack
          direction="row"
          spacing={1}
          className="no-print"
          justifyContent="flex-end"
          flexWrap="wrap"
        >
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => {
              if (publicPayload.data) {
                exportDashboardSnapshotCsv(
                  publicPayload.data,
                  dashboardYear
                );
              }
            }}
            disabled={!publicPayload.data}
          >
            Export CSV
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
          >
            Print
          </Button>
        </Stack>

        <div id="dashboard-print-root">
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            <Typography variant="h5" fontWeight={700}>
              Thuthuka — pipeline reporting
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Read-only intake statistics. Year {dashboardYear}.
            </Typography>
          </Stack>
          <ApplicationStatusReport
            variant="public"
            dashboardYear={dashboardYear}
            onDashboardYearChange={setDashboardYear}
            publicPayload={publicPayload}
            comparisonData={priorYearData}
          />
          <DisplayCharts
            variant="public"
            selectedYear={dashboardYear}
            publicData={publicPayload.data}
            publicLoading={publicPayload.loading}
          />
        </div>
      </Container>
    </Box>
  );
}
